// Helpers for the surf-conditions feature. The backend stores a metric/SI
// hourly series (meters, km/h, degrees, epoch seconds UTC) in
// tripConditions/{tripId}; everything here converts to surf-standard units
// (feet, knots, 16-point compass) and resolves the conditions at an exact
// moment so a clip/photo can be pinned to the hour it was shot.

export const M_TO_FT = 3.28084;
export const KMH_TO_KN = 0.539957;
export const KMH_TO_MPH = 0.621371;

export const mToFt = (m) => (m == null || !Number.isFinite(m) ? null : m * M_TO_FT);
export const kmhToKn = (k) => (k == null || !Number.isFinite(k) ? null : k * KMH_TO_KN);
export const kmhToMph = (k) => (k == null || !Number.isFinite(k) ? null : k * KMH_TO_MPH);

const COMPASS_16 = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
];

// 16-point compass label for a "coming from" bearing (how swell/wind is named).
export function compass16(deg) {
  if (deg == null || !Number.isFinite(deg)) return '';
  const i = Math.round((((deg % 360) + 360) % 360) / 22.5) % 16;
  return COMPASS_16[i];
}

// Normalize a bearing to a whole 0–359° value.
export const degInt = (deg) =>
  deg == null || !Number.isFinite(deg) ? null : Math.round((((deg % 360) + 360) % 360));

// Direction label combining the 16-point compass with the exact bearing, e.g.
// "WNW 292°" — period/swell direction matters precisely, so we show the degrees
// alongside the shorthand. Returns '' when unknown.
export function dirLabel(deg, withDeg = true) {
  if (deg == null || !Number.isFinite(deg)) return '';
  const c = compass16(deg);
  return withDeg ? `${c} ${degInt(deg)}°` : c;
}

// Rotation (deg) for an arrow that points the way the swell/wind is travelling.
// Provider bearings are "from", so travel direction is +180. The arrow glyph we
// use points up (north) at 0, so 180 means "coming from the north, heading south".
export const arrowRotation = (fromDeg) =>
  fromDeg == null || !Number.isFinite(fromDeg) ? 0 : (fromDeg + 180) % 360;

// Best-known capture time (epoch ms) for an asset: a manual override wins, then
// the probed recording date, then the upload time. Returns null if unknown.
export function assetTimeMs(asset) {
  if (!asset) return null;
  if (Number.isFinite(asset.capturedAtOverride)) return asset.capturedAtOverride;
  const rec = asset.recordedAt || asset.metadata?.recordedAt;
  if (rec) {
    const ms = Date.parse(rec);
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

// Whether the asset time was inferred (no real recorded date / override).
export const hasKnownTime = (asset) =>
  Number.isFinite(asset?.capturedAtOverride) ||
  !!(asset?.recordedAt || asset?.metadata?.recordedAt);

const lerp = (a, b, f) => a + (b - a) * f;
const num = (v) => (Number.isFinite(v) ? v : null);

// Resolve the conditions sample at an exact epoch (ms). Linearly interpolates
// magnitudes between the bracketing hours and takes the nearer hour for
// directions / tide state. Returns null if the time is outside the series.
export function sampleAt(cond, epochMs) {
  const hours = cond?.hours;
  if (!hours || !hours.length || !Number.isFinite(epochMs)) return null;
  const ts = epochMs / 1000;
  if (ts < hours[0].t - 3600 || ts > hours[hours.length - 1].t + 3600) return null;

  let lo = 0;
  let hi = hours.length - 1;
  if (ts <= hours[0].t) { lo = hi = 0; }
  else if (ts >= hours[hi].t) { lo = hi; }
  else {
    for (let i = 1; i < hours.length; i += 1) {
      if (hours[i].t >= ts) { lo = i - 1; hi = i; break; }
    }
  }
  const a = hours[lo];
  const b = hours[hi];
  const span = b.t - a.t || 1;
  const f = lo === hi ? 0 : Math.min(1, Math.max(0, (ts - a.t) / span));
  const near = f < 0.5 ? a : b;
  const blend = (k) => (num(a[k]) != null && num(b[k]) != null ? lerp(a[k], b[k], f) : num(near[k]));

  return {
    t: ts,
    waveH: blend('waveH'),
    waveDir: num(near.waveDir),
    waveP: blend('waveP'),
    swellH: blend('swellH'),
    swellDir: num(near.swellDir),
    swellP: blend('swellP'),
    swellPeak: blend('swellPeak'),
    swell2H: blend('swell2H'),
    swell2Dir: num(near.swell2Dir),
    swell2P: blend('swell2P'),
    swell2Peak: blend('swell2Peak'),
    swell3H: blend('swell3H'),
    swell3Dir: num(near.swell3Dir),
    swell3P: blend('swell3P'),
    swell3Peak: blend('swell3Peak'),
    windWaveH: blend('windWaveH'),
    windWaveDir: num(near.windWaveDir),
    windWaveP: blend('windWaveP'),
    windSpd: blend('windSpd'),
    windDir: num(near.windDir),
    windGust: blend('windGust'),
    tideM: blend('tideM'),
    tideState: near.tideState || null,
    tideStage01: blend('tideStage01'),
  };
}

// Circular (vector) mean of bearings, optionally weighted, in degrees.
function circularMean(items) {
  let x = 0;
  let y = 0;
  for (const it of items) {
    if (it.deg == null || !Number.isFinite(it.deg)) continue;
    const r = (it.deg * Math.PI) / 180;
    const w = it.w == null ? 1 : it.w;
    x += Math.cos(r) * w;
    y += Math.sin(r) * w;
  }
  if (x === 0 && y === 0) return null;
  let d = (Math.atan2(y, x) * 180) / Math.PI;
  if (d < 0) d += 360;
  return d;
}

const minMax = (vals) => {
  const v = vals.filter((x) => Number.isFinite(x));
  if (!v.length) return { min: null, max: null, avg: null };
  return {
    min: Math.min(...v),
    max: Math.max(...v),
    avg: v.reduce((s, x) => s + x, 0) / v.length,
  };
};

// Group a conditions series into local trip days using the spot's UTC offset.
export function groupByDay(cond) {
  if (!cond?.hours?.length) return [];
  const off = (cond.utcOffsetSec || 0) * 1000;
  const map = new Map();
  cond.hours.forEach((h) => {
    const localMs = h.t * 1000 + off;
    const key = Math.floor(localMs / 86_400_000);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(h);
  });
  const days = Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  return days.map(([key, hours]) => {
    // Render the label from a UTC date shifted by the offset so it reads as the
    // spot's local calendar day regardless of the viewer's timezone.
    const labelMs = key * 86_400_000 - off + 12 * 3600 * 1000;
    return {
      key,
      startMs: hours[0].t * 1000,
      endMs: hours[hours.length - 1].t * 1000,
      label: new Date(labelMs).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }),
      hours,
      summary: summarizeDay(hours),
    };
  });
}

// Summarize one swell partition (primary/secondary/tertiary) over a set of hours.
// When `peakKey` is given and has data, the reported period is the peak period
// of the dominant train (Surfline-style) rather than the spectral mean.
function summarizeSwell(hours, hKey, dirKey, pKey, peakKey) {
  const h = minMax(hours.map((x) => x[hKey]));
  const meanP = minMax(hours.map((x) => x[pKey]));
  const peakP = peakKey ? minMax(hours.map((x) => x[peakKey])) : { avg: null };
  const dir = circularMean(hours.map((x) => ({ deg: x[dirKey], w: x[hKey] || 0.1 })));
  return {
    minFt: mToFt(h.min),
    maxFt: mToFt(h.max),
    dir,
    periodAvg: peakP.avg != null ? peakP.avg : meanP.avg,
    meanPeriodAvg: meanP.avg,
    has: h.max != null && h.max > 0,
  };
}

// Wind summary (knots + dominant direction) over a set of hours — used both for
// a whole day and, more usefully, for a specific surf-session window.
export function windStats(hours) {
  const list = Array.isArray(hours) ? hours : [];
  const w = minMax(list.map((h) => h.windSpd));
  const gust = minMax(list.map((h) => h.windGust));
  // Equal-weight (per-hour) circular mean so the session's dominant direction
  // isn't hijacked by a single strong, off-direction hour (e.g. an end-of-session
  // sea-breeze spike making a mostly-NNE morning read like the last gust).
  const dir = circularMean(list.map((h) => ({ deg: h.windDir })));
  return {
    minKn: kmhToKn(w.min),
    maxKn: kmhToKn(w.max),
    avgKn: kmhToKn(w.avg),
    gustMaxKn: kmhToKn(gust.max),
    dir,
    has: w.max != null,
  };
}

// Compact dominant/range summary for a set of hours (one trip day).
export function summarizeDay(hours) {
  const wind = minMax(hours.map((h) => h.windSpd));
  const tide = minMax(hours.map((h) => h.tideM));
  const windDir = circularMean(hours.map((h) => ({ deg: h.windDir, w: h.windSpd || 0.1 })));

  // Up to three swell trains, ordered primary → tertiary, kept only when present.
  // The primary train reports peak period (swellPeak) when available.
  const p1 = summarizeSwell(hours, 'swellH', 'swellDir', 'swellP', 'swellPeak');
  const p2 = summarizeSwell(hours, 'swell2H', 'swell2Dir', 'swell2P');
  const p3 = summarizeSwell(hours, 'swell3H', 'swell3Dir', 'swell3P');
  const swells = [p1, p2, p3].filter((s) => s.has);

  return {
    // Primary swell (kept as the existing fields for back-compat).
    swellMinFt: p1.minFt,
    swellMaxFt: p1.maxFt,
    swellDir: p1.dir,
    periodAvg: p1.periodAvg,
    swells,
    windMinKn: kmhToKn(wind.min),
    windMaxKn: kmhToKn(wind.max),
    windDir,
    tideMinFt: mToFt(tide.min),
    tideMaxFt: mToFt(tide.max),
    hasSwell: p1.has,
    hasWind: wind.max != null,
    hasTide: tide.max != null,
  };
}

export const tideStateLabel = (s) =>
  s === 'rising' ? 'Rising' : s === 'falling' ? 'Falling' : s === 'slack' ? 'Slack' : '';
export const tideStateArrow = (s) => (s === 'rising' ? '▲' : s === 'falling' ? '▼' : '·');

// Tide extremes (high/low) that fall on a given local day, formatted for labels.
export function dayExtremes(cond, dayHours) {
  if (!cond?.tideExtremes?.length || !dayHours?.length) return [];
  const lo = dayHours[0].t;
  const hi = dayHours[dayHours.length - 1].t;
  return cond.tideExtremes
    .filter((e) => e.t >= lo - 1800 && e.t <= hi + 1800)
    .map((e) => ({ ...e, heightFt: mToFt(e.heightM) }));
}

// Format an epoch-seconds time in the spot's local timezone (HH:MM).
export function localTime(cond, tSec) {
  if (!Number.isFinite(tSec)) return '';
  const off = (cond?.utcOffsetSec || 0) * 1000;
  return new Date(tSec * 1000 + off).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

// Color ramp for swell size (ft) — calm teal up to a heavy ocean blue, matching
// the globe palette (#0b4f73 deep, cyan highlights).
export function swellColor(ft) {
  if (ft == null) return 'var(--muted)';
  if (ft < 1) return '#5bd6c0';
  if (ft < 2.5) return '#34b3d6';
  if (ft < 5) return '#3aa0ff';
  if (ft < 8) return '#2f6fe0';
  return '#1f3f9e';
}

// Wind color: light green (clean) -> amber/red (blown out).
export function windColor(kn) {
  if (kn == null) return 'var(--muted)';
  if (kn < 6) return '#43d17f';
  if (kn < 12) return '#9bd34a';
  if (kn < 18) return '#e6c34a';
  return '#e0734a';
}

export const fmtFt = (ft, d = 1) =>
  ft == null ? '—' : `${ft.toFixed(d)} ft`;
export const fmtKn = (kn) => (kn == null ? '—' : `${Math.round(kn)} kn`);
export const fmtPeriod = (s) => (s == null ? '—' : `${Math.round(s)}s`);

const FT_TO_M = 1 / M_TO_FT;
const MPH_TO_KMH = 1 / KMH_TO_MPH;

// Per-day manual spec defaults (swell + wind + a daily tide low/high).
export const DAY_SPEC_DEFAULTS = {
  swellFt: 6,
  periodS: 18,
  swellDirDeg: 210,
  windMph: 3,
  windDirDeg: 45, // NE (coming-from bearing)
  tideLowFt: 1,
  tideHighFt: 2,
  lowHour: 5,
  highHour: 11,
};

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (ms) => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};
const parseYmd = (s) => {
  const [y, m, d] = String(s || '').split('-').map((n) => parseInt(n, 10));
  return Number.isFinite(y) ? Date.UTC(y, (m || 1) - 1, d || 1) : null;
};

// The inclusive list of trip days ('YYYY-MM-DD') from start/end. Falls back to a
// few generic days when the trip has no dates yet, so manual entry still works.
export function tripDateList(startDate, endDate, fallbackDays = 3) {
  const s = parseYmd(startDate);
  if (s == null) {
    const base = Date.UTC(2026, 0, 1);
    return Array.from({ length: fallbackDays }, (_, i) => ymd(base + i * 86_400_000));
  }
  let e = parseYmd(endDate);
  if (e == null || e < s) e = s + (fallbackDays - 1) * 86_400_000;
  const out = [];
  for (let t = s; t <= e; t += 86_400_000) out.push(ymd(t));
  return out;
}

// Parse a "HH:MM" clock string to minutes-since-midnight (null if invalid).
export function parseHM(s) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || '').trim());
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(mm) || h > 23 || mm > 59) return null;
  return h * 60 + mm;
}

// Convert the trip's surf-session log (a map of local-date -> [{start,end,note}],
// in spot-local clock time) into absolute epoch-second windows, using the
// conditions series' UTC offset so they line up on the timeline. The stored
// hours treat local time as UTC when offset is 0 (manual entry), and as true
// UTC when offset is set (provider data) — both handled by `local - offset`.
export function sessionWindows(sessions, utcOffsetSec = 0) {
  if (!sessions || typeof sessions !== 'object') return [];
  const off = utcOffsetSec || 0;
  const out = [];
  Object.entries(sessions).forEach(([date, list]) => {
    const day0 = parseYmd(date);
    if (day0 == null) return;
    (Array.isArray(list) ? list : []).forEach((s) => {
      const a = parseHM(s.start);
      const b = parseHM(s.end);
      if (a == null || b == null) return;
      const base = day0 / 1000 - off;
      let startSec = base + a * 60;
      let endSec = base + b * 60;
      if (endSec <= startSec) endSec += 86_400; // crossed midnight (dawn-to-after-dark)
      out.push({ date, startSec, endSec, start: s.start, end: s.end, note: s.note || '' });
    });
  });
  return out.sort((x, y) => x.startSec - y.startSec);
}

// A friendly weekday label for a 'YYYY-MM-DD' date.
export function dayLabel(date) {
  const ms = parseYmd(date);
  if (ms == null) return date || '';
  return new Date(ms + 12 * 3600 * 1000).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

// Build a synthetic conditions series from per-day human inputs, for manual entry
// / previewing the visualization when no provider data exists. Each day carries
// its own swell / wind / tide, pinned to that day's calendar date, so the series
// lines up exactly with the trip's days. Swell & wind are held constant within a
// day; the tide is a smooth semidiurnal sine through that day's low/high. Stored
// with utcOffsetSec = 0, so the epoch hours map 1:1 to the clock the user typed.
//
//   days: [{ date:'YYYY-MM-DD', swellFt, periodS, swellDirDeg, windMph,
//            windDirDeg, tideLowFt, tideHighFt, lowHour, highHour }, ...]
export function buildManualConditions({ days } = {}) {
  const specs = (Array.isArray(days) ? days : []).filter(Boolean);
  if (!specs.length) return null;

  const hours = [];
  const tideExtremes = [];

  specs.forEach((raw) => {
    const s = { ...DAY_SPEC_DEFAULTS, ...raw };
    const day0Ms = parseYmd(s.date);
    const day0Sec = (day0Ms == null ? Date.UTC(2026, 0, 1) : day0Ms) / 1000;

    const mid = (s.tideHighFt + s.tideLowFt) / 2;
    const amp = (s.tideHighFt - s.tideLowFt) / 2;
    const periodH = Math.max(1, Math.abs(s.highHour - s.lowHour) * 2) || 12;
    const tideFtAt = (h) => mid + amp * Math.cos((2 * Math.PI * (h - s.highHour)) / periodH);

    for (let h = 0; h < 24; h += 1) {
      const tNow = tideFtAt(h);
      const delta = tideFtAt(h + 1) - tNow;
      const tideState = Math.abs(delta) < 0.02 ? 'slack' : delta > 0 ? 'rising' : 'falling';
      hours.push({
        t: day0Sec + h * 3600,
        swellH: s.swellFt * FT_TO_M,
        swellP: s.periodS,
        swellDir: s.swellDirDeg,
        waveH: s.swellFt * FT_TO_M,
        waveP: s.periodS,
        waveDir: s.swellDirDeg,
        windSpd: s.windMph * MPH_TO_KMH,
        windGust: s.windMph * 1.3 * MPH_TO_KMH,
        windDir: s.windDirDeg,
        tideM: tNow * FT_TO_M,
        tideState,
      });
    }

    // Tide highs/lows falling within this calendar day (for chart labels).
    for (let k = -1; k * periodH <= 24 + periodH; k += 1) {
      const lowAbs = s.lowHour + k * periodH;
      const highAbs = s.highHour + k * periodH;
      if (lowAbs >= 0 && lowAbs < 24) {
        tideExtremes.push({ t: day0Sec + lowAbs * 3600, type: 'low', heightM: s.tideLowFt * FT_TO_M });
      }
      if (highAbs >= 0 && highAbs < 24) {
        tideExtremes.push({ t: day0Sec + highAbs * 3600, type: 'high', heightM: s.tideHighFt * FT_TO_M });
      }
    }
  });

  hours.sort((a, b) => a.t - b.t);
  tideExtremes.sort((a, b) => a.t - b.t);

  return {
    status: 'ready',
    manual: true,
    hours,
    tideExtremes,
    rangeStart: hours[0].t,
    rangeEnd: hours[hours.length - 1].t,
    utcOffsetSec: 0,
    source: { tide: 'manual', swell: 'manual', wind: 'manual' },
  };
}
